import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import KineticLines from "@/components/motion/KineticLines";
import Reveal from "@/components/motion/Reveal";
import {
  Mushroom,
  Leaf,
  Snake,
  Gecko,
  Turtle,
  BeardedDragon,
  CurledSnake,
  SnakeS,
  SnakeEgg,
  Fern,
  Berries,
  Acorn,
  CircleStamp,
  Bat,
  Rose,
  Coffin,
  Candelabra,
  SpiderWeb,
  Ghost,
} from "@/components/decor/SvgIcons";
import { ArrowRight, HandHeart, GraduationCap, Leaf as LeafIcon, ShieldCheck, Sparkle as SparklePh, MagicWand } from "@phosphor-icons/react";

const WHY = [
  { icon: HandHeart, title: "Hands-on learning", copy: "Guided touch time with calm, socialized ambassadors." },
  { icon: ShieldCheck, title: "Professional animal care", copy: "Zoo-grade animal care & educated staff." },
  { icon: GraduationCap, title: "Educational presentations", copy: "Curriculum-aligned science storytelling." },
  { icon: LeafIcon, title: "Conservation focus", copy: "Every myth debunked helps a wild reptile thrive." },
  { icon: SparklePh, title: "Memorable experiences", copy: "Photos, giggles, and lifelong wonder for every age." },
  { icon: MagicWand, title: "Custom programs", copy: "Themes, ages, and event types tailored to you." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const ySnake = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yGecko = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yFrog = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div className="pt-28">
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative overflow-hidden pb-16 md:pb-28" data-testid="home-hero">
        {/* Backdrop bloom */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-24 -left-20 w-[520px] h-[520px] rounded-full bg-lavender/25 blur-3xl" />
          <div className="absolute top-32 -right-24 w-[420px] h-[420px] rounded-full bg-sage/40 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-[520px] h-[420px] rounded-full bg-plum/15 blur-3xl" />
        </div>

        {/* Reptile clip art sprinkled */}
        <div className="absolute top-32 left-3 opacity-70 hidden md:block" aria-hidden><Fern size={90} /></div>
        <div className="absolute bottom-24 left-6 opacity-80 hidden md:block" aria-hidden><CurledSnake size={80} /></div>
        <div className="absolute top-20 left-1/2 opacity-70 hidden md:block" aria-hidden><Acorn size={38} /></div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10 pt-6 md:pt-12 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 relative">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="chip mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-plum" /> Now booking — Southern California
              </motion.div>

              <KineticLines
                as="h1"
                lines={["Where Curiosity", "Meets Conservation."]}
                italicIdx={1}
                className="font-display text-forest text-[15vw] leading-[0.95] md:text-[8.2vw] lg:text-[7rem] xl:text-[8.5rem] tracking-[-0.03em]"
              />

              <Reveal delay={0.5} y={20}>
                <p className="mt-8 max-w-xl text-lg md:text-xl text-forest/80 leading-relaxed font-body">
                  Spooky Scales brings reptiles &amp; amphibians to schools, libraries, birthday parties,
                  festivals, homeschool groups, scout troops, conventions, and community events —
                  through fun, interactive educational experiences.
                </p>
              </Reveal>

              <Reveal delay={0.7} y={20} className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/booking" className="btn-pill btn-primary" data-testid="hero-cta-book">
                  Book an Event <ArrowRight size={18} weight="bold" />
                </Link>
                <Link to="/animals" className="btn-pill btn-secondary" data-testid="hero-cta-animals">
                  Meet Our Animals
                </Link>
              </Reveal>
            </div>

            {/* Reptile-forward collage */}
            <div className="lg:col-span-5 relative min-h-[560px]">
              <motion.div
                style={{ y: ySnake }}
                initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
                animate={{ opacity: 1, scale: 1, rotate: -3 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
                className="absolute top-0 left-0 md:left-2"
              >
                <div className="relative">
                  <img
                    src="/animals/spooky.webp"
                    alt="Spooky, a snow corn snake"
                    loading="lazy"
                    className="blob-a w-[280px] h-[340px] md:w-[320px] md:h-[380px] object-cover shadow-2xl border-4 border-cream"
                  />
                  <div className="absolute -bottom-4 -left-3 font-script text-3xl text-plum rotate-[-6deg]">meet Spooky →</div>
                </div>
              </motion.div>

              <motion.div
                style={{ y: yGecko }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
                className="absolute bottom-0 right-0"
              >
                <img
                  src="/animals/kerchak.webp"
                  alt="Kerchak, an African fat-tailed gecko"
                  loading="lazy"
                  className="blob-c w-[200px] h-[240px] md:w-[230px] md:h-[280px] object-cover shadow-2xl border-4 border-cream"
                />
              </motion.div>

              <motion.div
                style={{ y: yFrog }}
                initial={{ opacity: 0, scale: 0.85, rotate: 8 }}
                animate={{ opacity: 1, scale: 1, rotate: 4 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
                className="absolute top-24 right-0 md:right-6"
              >
                <img
                  src="/animals/tarot.webp"
                  alt="Tarot, a leatherback bearded dragon"
                  loading="lazy"
                  className="blob-b w-[160px] h-[160px] md:w-[180px] md:h-[180px] object-cover shadow-xl border-4 border-cream"
                />
              </motion.div>

              <div className="absolute bottom-4 left-2 opacity-80"><Mushroom size={60} /></div>
              <div className="absolute -bottom-6 -left-6 hidden md:block opacity-70">
                <CircleStamp text="· educational reptile outreach · southern california · est. 2019 · " size={150} />
              </div>
              <div className="absolute top-6 -left-2 opacity-80 hidden md:block" aria-hidden><SnakeEgg size={40} /></div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ABOUT / MANIFESTO ============ */}
      <section className="py-24 md:py-36 relative" data-testid="home-about">
        <div className="absolute top-10 right-6 opacity-70 hidden md:block" aria-hidden><Gecko size={110} /></div>
        <div className="absolute bottom-10 left-8 opacity-80 hidden md:block" aria-hidden><Rose size={80} /></div>
        <div className="absolute top-4 left-1/2 opacity-70 hidden md:block" aria-hidden><Ghost size={56} /></div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <div className="chip mb-6">Chapter I · Our Cottage</div>
              <h2 className="font-display text-5xl md:text-6xl text-forest tracking-tight leading-[1.05]">
                A gentle home for <em className="text-plum">misunderstood</em> creatures.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-forest/85 leading-relaxed">
                Spooky Scales is a small, passionate outreach cottage in Southern California dedicated to educating people about
                reptiles and amphibians while promoting <strong>conservation</strong>,{" "}
                <strong>ethical ownership</strong>, and appreciation for animals that are so often feared.
                Every one of our ambassadors is <strong>rescued or acquired from a local, ethical breeder</strong> —
                and each is a beloved member of our family, not a prop.
              </p>
              <div className="mt-8 grid md:grid-cols-2 gap-4">
                <div className="grim-card p-6">
                  <div className="font-script text-plum text-2xl">Habitat-first ambassadors</div>
                  <p className="text-sm text-forest/80 mt-2 leading-relaxed">
                    Whenever possible, our ambassadors are shown in natural-habitat enclosures and
                    enrichment stands that support their comfort, wellbeing, and natural behaviors.
                  </p>
                </div>
                <div className="grim-card p-6">
                  <div className="font-script text-plum text-2xl">Guidance for every keeper</div>
                  <p className="text-sm text-forest/80 mt-2 leading-relaxed">
                    Consultations, species-specific husbandry guides, and — for local clients —
                    custom habitat builds. Don't see your species? Reach out; we're always happy to help.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section className="py-20 md:py-24 relative" data-testid="home-why">
        <div className="absolute top-20 left-4 opacity-60 hidden md:block" aria-hidden><Turtle size={110} /></div>
        <div className="absolute top-8 right-8 opacity-80 hidden md:block" aria-hidden><Candelabra size={90} /></div>
        <div className="absolute bottom-16 right-6 opacity-60 hidden md:block" aria-hidden><BeardedDragon size={130} /></div>
        <div className="absolute bottom-4 left-1/3 opacity-70 hidden md:block" aria-hidden><Coffin size={70} /></div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <div className="chip mb-3">Chapter II · Six Reasons</div>
              <h2 className="font-display text-4xl md:text-5xl text-forest leading-[1.02]">
                Why families &amp; schools <em>choose us.</em>
              </h2>
            </div>
            <div className="hidden md:block chapter-num text-[8rem]">02</div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="grim-card p-5 h-full flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-sage/40 flex items-center justify-center text-forest">
                    <w.icon size={22} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-forest/55">0{i + 1}</div>
                    <h3 className="font-display text-xl text-forest leading-snug mt-0.5">{w.title}</h3>
                    <p className="mt-1 text-sm text-forest/75 leading-relaxed">{w.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURED ANIMAL ============ */}
      <section className="py-24 md:py-32 relative" data-testid="home-featured">
        <div className="absolute top-16 right-10 opacity-70 hidden md:block" aria-hidden><Fern size={110} /></div>
        <div className="absolute top-6 left-8 opacity-80 hidden md:block" aria-hidden><Bat size={70} /></div>
        <div className="absolute bottom-10 left-6 opacity-70 hidden md:block" aria-hidden><SnakeS size={90} /></div>
        <div className="absolute bottom-6 right-1/3 opacity-80 hidden md:block" aria-hidden><Rose size={64} /></div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 relative">
              <div className="chapter-num text-[9rem] absolute -top-16 -left-2">03</div>
              <div className="chip mb-5 relative">Chapter III · Featured</div>
              <h2 className="font-display text-5xl md:text-6xl text-forest leading-[1.02]">
                This month's <em className="text-plum">ambassador.</em>
              </h2>
              <p className="mt-5 text-forest/80 text-lg max-w-md">
                Boo is our bashful ball python — spooky in name only. When she isn't rolled into a
                perfect cuddle-ball, she's busy charming first-time snake friends.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
                <div className="grim-card p-4">
                  <div className="text-xs uppercase tracking-widest text-forest/60">Favorite snack</div>
                  <div className="font-display italic text-forest text-xl mt-1">Frozen-thawed rats</div>
                </div>
                <div className="grim-card p-4">
                  <div className="text-xs uppercase tracking-widest text-forest/60">Mood today</div>
                  <div className="font-display italic text-forest text-xl mt-1">Snug &amp; serene</div>
                </div>
              </div>
              <Link to="/animals" className="btn-pill btn-secondary mt-8" data-testid="featured-cta">
                Meet the whole crew <ArrowRight size={18} />
              </Link>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="relative">
                <img
                  src="/animals/boo.webp"
                  alt="Boo the ball python"
                  loading="lazy"
                  className="blob-b w-full aspect-[4/5] object-cover shadow-2xl border-4 border-cream"
                />
                <div className="absolute top-4 right-6 font-script text-3xl text-plum rotate-6">Boo, ball python</div>
                <div className="absolute -bottom-6 -left-6 opacity-70"><Leaf size={70} /></div>
                <div className="absolute -top-6 -right-4 opacity-80"><Snake size={90} className="rotate-12" /></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
